import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MapPin, Plus, Search, Edit, Trash2, Eye, X,
  Building2, CheckCircle2, FileText, Globe
} from 'lucide-react';

interface City {
  id: number;
  name: string;
  governorate?: string;
  postalCode?: string;
  countryCode?: string;
  zoneId?: number;
  zone?: string;
  isActive: boolean;
  createdAt?: string;
}

export default function CitiesPage() {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [form, setForm] = useState({
    name: '', governorate: '', postalCode: '', countryCode: 'EG', zoneId: undefined, isActive: true
  });

  useEffect(() => { loadCities(); }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/cities?pageSize=1000');
      if (response.data.success) {
        const data = response.data.data;
        setCities(Array.isArray(data) ? data : (data?.items || []));
      } else {
        // API returns array directly
        setCities(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error loading cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCity(null);
    setForm({ name: '', governorate: '', postalCode: '', countryCode: 'EG', zoneId: undefined, isActive: true });
    setShowModal(true);
  };

  const openEditModal = (city: City) => {
    setEditingCity(city);
    setForm({
      name: city.name || '',
      governorate: city.governorate || '',
      postalCode: city.postalCode || '',
      countryCode: city.countryCode || 'EG',
      zoneId: city.zoneId,
      isActive: city.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCity) {
        console.log('FORM', form);
        await api.put('/api/cities/' + editingCity.id, form);
        alert('تم تحديث بيانات المدينة بنجاح');
      } else {
        await api.post('/api/cities', form);
        alert('تم إضافة المدينة بنجاح');
      }
      setShowModal(false);
      loadCities();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ بيانات المدينة');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المدينة؟')) return;
    try {
      await api.delete('/api/cities/' + id);
      loadCities();
      alert('تم حذف المدينة بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف المدينة');
    }
  };

  const filteredCities = Array.isArray(cities) ? cities.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.governorate || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.postalCode || '').includes(search)
  ) : [];

  const stats = {
    total: cities.length,
    active: cities.filter(c => c.isActive !== false).length,
    governorates: new Set(cities.map(c => c.governorate).filter(g => g)).size
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <MapPin className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة المدن</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة المدن والمناطق الجغرافية</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة مدينة جديدة</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <MapPin className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المدن</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <CheckCircle2 className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المدن النشطة</p>
            <p className='text-2xl font-bold text-green-700'>{stats.active}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <Building2 className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المحافظات</p>
            <p className='text-2xl font-bold text-blue-700'>{stats.governorates}</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث باسم المدينة، المحافظة، أو الرمز البريدي...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل المدن...</p>
            </div>
          ) : filteredCities.length === 0 ? (
            <div className='p-12 text-center'>
              <MapPin className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا توجد مدن</h3>
              <p className='text-gray-500'>ابدأ بإضافة مدينتك الأولى</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>اسم المدينة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المحافظة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الرمز البريدي</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الدولة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCities.map((city) => (
                    <tr key={city.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold'>
                            <MapPin className='w-5 h-5' />
                          </div>
                          <div>
                            <p className='font-semibold text-gray-900'>{city.name}</p>
                            {city.createdAt && (
                              <p className='text-xs text-gray-500'>منذ {new Date(city.createdAt).toLocaleDateString('ar-EG')}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{city.governorate || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-mono text-gray-700'>{city.postalCode || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-2'>
                          <Globe className='w-4 h-4 text-gray-400' />
                          <span className='text-gray-700'>{city.countryCode || '-'}</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + (
                          city.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        )}>
                          {city.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedCity(city); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(city)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(city.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingCity ? 'تعديل بيانات المدينة' : 'إضافة مدينة جديدة'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>اسم المدينة *</Label>
                    <Input
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='أدخل اسم المدينة'
                    />
                  </div>
                  <div>
                    <Label>المحافظة</Label>
                    <Input
                      value={form.governorate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, governorate: e.target.value })}
                      className={inputClass}
                      placeholder='المحافظة'
                    />
                  </div>
                  <div>
                    <Label>الرمز البريدي</Label>
                    <Input
                      value={form.postalCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, postalCode: e.target.value })}
                      className={inputClass}
                      placeholder='الرمز البريدي'
                    />
                  </div>
                  <div>
                    <Label>رمز الدولة</Label>
                    <Input
                      value={form.countryCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, countryCode: e.target.value })}
                      className={inputClass}
                      placeholder='EG'
                    />
                  </div>
                  <div className='flex items-center gap-2 md:col-span-2'>
                    <input
                      type='checkbox'
                      id='isActive'
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className='w-4 h-4'
                    />
                    <label htmlFor='isActive' className='text-sm font-medium'>مدينة نشطة</label>
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingCity ? 'تحديث البيانات' : 'إضافة المدينة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedCity && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل المدينة</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white'>
                    <MapPin className='w-8 h-8' />
                  </div>
                  <div>
                    <h3 className='text-xl font-bold'>{selectedCity.name}</h3>
                    <p className='text-sm text-gray-500'>
                      {selectedCity.isActive !== false ? 'مدينة نشطة' : 'مدينة غير نشطة'}
                      {selectedCity.createdAt && ' " منذ ' + new Date(selectedCity.createdAt).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المحافظة</p>
                    <p className='font-semibold'>{selectedCity.governorate || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الرمز البريدي</p>
                    <p className='font-semibold font-mono'>{selectedCity.postalCode || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>الدولة</p>
                    <p className='font-semibold'>{selectedCity.countryCode || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>المنطقة</p>
                    <p className='font-semibold'>{selectedCity.zone || '-'}</p>
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedCity); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل البيانات
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
